import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { entraSettings } from "./entra-settings.js";
import { customerOperationError } from "./error-presentation.js";
import { signInFlowKey } from "./sign-in-contract.js";
import { createNativeAuthClient, nativeAuthenticationEnabled, nativeAccount, nativeAccountKey } from "./native-auth-client.js";
let application;
let runtime;
let nativeClient;
let authenticationBlocked = false;
export async function initializeAuthentication(configuration) {
  try {
    runtime = configuration; nativeClient = undefined; authenticationBlocked = false;
    application = new PublicClientApplication(entraSettings(configuration, window.location.origin));
    await application.initialize();
    let pendingFlow;
    try { pendingFlow = JSON.parse(window.sessionStorage?.getItem?.(signInFlowKey) || "null"); } catch {}
    if (pendingFlow?.providerCompletionPending) {
      authenticationBlocked = true;
      // OAuth caches its result before our proof-bound confirmation. A reload
      // or interrupted handoff must not turn that unconfirmed result into a
      // restored subscription/workspace session.
      const unconfirmed = application.getAllAccounts().find(item => item.homeAccountId === pendingFlow.unconfirmedAccountId);
      if (unconfirmed) await application.clearCache({ account: unconfirmed });
      return null;
    }
    const expectedId = window.sessionStorage?.getItem?.(nativeAccountKey);
    if (expectedId && nativeAuthenticationEnabled(configuration)) {
      nativeClient = await createNativeAuthClient(configuration, window.location.origin);
      // The SDK retrieves its first cached account. Refuse ambiguous caches or
      // an account switch, rather than refreshing another person's session.
      const account = nativeAccount(nativeClient, expectedId).getAccount();
      application.setActiveAccount(account);
    }
    return currentAccount();
  } catch (cause) {
    application = undefined; nativeClient = undefined;
    throw customerOperationError("pirdy_authentication_initialization_failed", cause);
  }
}
export function currentAccount() {
  if (!application || authenticationBlocked) return null;
  const active = application.getActiveAccount();
  if (active) return active;
  const accounts = application.getAllAccounts();
  // Never arbitrarily choose between identities on a shared computer.
  if (accounts.length !== 1) return null;
  application.setActiveAccount(accounts[0]);
  return accounts[0];
}
async function authenticate(prompt, intent = "signin", { preserveWindow = false } = {}) {
  const state=[...crypto.getRandomValues(new Uint8Array(32))].map(x=>x.toString(16).padStart(2,"0")).join("");
  if (!preserveWindow) {
    window.sessionStorage.setItem(signInFlowKey, JSON.stringify({ state, started: Date.now(), mode: "redirect", intent }));
    // Back may revive this document with its awaited promise still pending.
    // Reload that cache entry so the launch controls/session are reinitialized.
    const restore = event => { if (event.persisted) window.location.reload(); };
    window.addEventListener("pageshow", restore);
    try { window.location.assign('/sign-in.html#flow=' + state + '&mode=redirect&intent=' + intent); }
    catch (error) { window.removeEventListener("pageshow", restore); throw error; }
    // Do not run account-creation/workspace mutations while navigation begins.
    return await new Promise(() => {});
  }
  const popup=window.open(`/sign-in.html#flow=${state}&intent=${intent}`,`pirdy-signin-${state}`,"popup,width=520,height=760");
  if(!popup)throw new Error("Allow the Pirdy sign-in window in your browser and try again.");
  const result=await new Promise((resolve,reject)=>{
    const started=Date.now();let timer;
    const cleanup=()=>{window.removeEventListener("message",receive);clearInterval(timer);};
    const receive=event=>{if(event.origin!==window.location.origin||event.source!==popup||!["pirdy-signin-complete","pirdy-signin-cancelled"].includes(event.data?.type)||event.data.state!==state)return;
      cleanup();if(event.data.type==="pirdy-signin-cancelled"){reject(new Error("Sign-in was cancelled."));return;}if(!event.data.result?.account||typeof event.data.result.accessToken!=="string")reject(new Error("Sign-in was incomplete."));else resolve(event.data.result);};
    window.addEventListener("message",receive);
    timer=setInterval(()=>{if(popup.closed||Date.now()-started>30*60*1000){cleanup();reject(new Error("Sign-in was cancelled or expired."));}},500);
  });
  await application.hydrateCache(result,{scopes:["openid","profile","email",runtime.apiScope]});
  application.setActiveAccount(result.account);
  // This result came from hosted PKCE. The opener must also leave its prior
  // native-auth refresh mode; sessionStorage is isolated from the popup.
  nativeClient = undefined; authenticationBlocked = false;
  window.sessionStorage?.removeItem?.(nativeAccountKey); window.sessionStorage?.removeItem?.(signInFlowKey);
  return result.account;
}
export async function signIn(options) {
  try { return await authenticate("login", "signin", options); }
  catch (cause) { throw customerOperationError("pirdy_sign_in_failed", cause); }
}
export async function createAccount(options) {
  try { return await authenticate("login", "create", options); }
  catch (cause) { throw customerOperationError("pirdy_account_creation_failed", cause); }
}
export async function verifyAccountForProviderLink() {
  const before = currentAccount();
  const result = await application.acquireTokenPopup({
    scopes: ["openid", "profile", "email", runtime.apiScope], prompt: "login",
    loginHint: before?.username || undefined,
    extraQueryParameters: { max_age: "0" },
    claims: JSON.stringify({ access_token: { auth_time: { essential: true } }, id_token: { auth_time: { essential: true } } }),
    redirectUri: `${window.location.origin}/auth-callback.html`,
  });
  if (before && before.homeAccountId !== result.account?.homeAccountId)
    throw new Error("You verified a different account. Sign in with the existing Pirdy account you intend to keep.");
  application.setActiveAccount(result.account);
  return { account: result.account, token: result.accessToken };
}
export async function signOut() {
  const account = currentAccount();
  if (!account) return;
  try {
    if (nativeClient) {
      const current = nativeAccount(nativeClient, account.homeAccountId);
      window.sessionStorage.removeItem(nativeAccountKey);
      const result = await current.signOut();
      if (result.isFailed?.()) throw new Error("Sign-out could not complete.");
      nativeClient = undefined; application.setActiveAccount(null);
    } else await application.logoutPopup({ account, postLogoutRedirectUri: window.location.origin });
  }
  catch (cause) { throw customerOperationError("pirdy_sign_out_failed", cause); }
}
export async function accessToken() {
  const account = currentAccount();
  if (!account) throw new Error("Sign in to use Pirdy Cloud Control.");
  const request = { account, scopes: [runtime.apiScope] };
  try {
    if (nativeClient) {
      const result = await nativeAccount(nativeClient, account.homeAccountId).getAccessToken({ scopes: request.scopes, forceRefresh: false });
      if (!result.isCompleted?.() || !result.data?.accessToken || result.data.account?.homeAccountId !== account.homeAccountId)
        throw new Error("Sign in again to continue using Pirdy Cloud Control.");
      return result.data.accessToken;
    }
    try { return (await application.acquireTokenSilent(request)).accessToken; }
    catch (cause) {
      if (!(cause instanceof InteractionRequiredAuthError)) throw cause;
      return (await application.acquireTokenPopup({ ...request,
        redirectUri: `${window.location.origin}/auth-callback.html`,
      })).accessToken;
    }
  } catch (cause) { throw customerOperationError("pirdy_token_failed", cause); }
}
